import { useState } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';

type Props = {
  disabled?: boolean;
  guidelineId: string;
  guidelineName: string;
};

export const Delete = ({ disabled, guidelineId, guidelineName }: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { navigate } = useRouting();
  const [deleteInput, setDeleteInput] = useState('');

  const [deleteGuideline, { isLoading: isDeletingGuideline }] =
    api.endpoints.deleteGuideline.useMutation();

  const [confirmDelete, showConfirmDelete, hideConfirmDelete] = useModal({
    title: intl.formatMessage(
      {
        id: 'guidelines.details.delete.confirmTitle',
        defaultMessage: 'Delete {guideline}',
        description:
          'Title for delete guideline confirmation modal. E.g Delete Contract Review',
      },
      {
        guideline: guidelineName,
      },
    ),
    primaryAction: {
      text: intl.formatMessage({
        id: 'guidelines.details.delete.confirmPrimaryAction',
        defaultMessage: 'Delete',
        description:
          'Label for primary action button in delete guideline confirmation modal',
      }),
      onClick: async () => {
        try {
          await deleteGuideline({ guidelineId });
          hideConfirmDelete();
          navigate('/guidelines');
        } catch {
          toast({
            status: 'danger',
            title: intl.formatMessage({
              id: 'guidelines.details.delete.errorToastTitle',
              defaultMessage: 'Error Deleting Guideline',
              description:
                'Title for toast notification when deleting a guideline fails',
            }),
            message: intl.formatMessage({
              id: 'guidelines.details.delete.errorToastMessage',
              defaultMessage:
                'There was an error deleting the guideline. Please try again.',
              description:
                'Message for toast notification when deleting a guideline fails',
            }),
          });
        }
      },
      isLoading: isDeletingGuideline,
      disabled: deleteInput !== guidelineName,
      variant: 'danger',
    },
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage({
            id: 'guidelines.details.delete.confirmMessage',
            defaultMessage:
              'This action will permanently delete the selected guideline and all its contents. You will not be able to recover it later. Type the guideline name below to confirm.',
            description: 'Message in delete guideline confirmation modal',
          })}
        </Text>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'guidelines.details.delete.confirmInputLabel',
            defaultMessage: 'Guideline Name',
            description: 'Label for input field to confirm guideline deletion',
          })}
          placeholder={intl.formatMessage({
            id: 'guidelines.details.delete.confirmInputPlaceholder',
            defaultMessage: 'Enter guideline name',
            description:
              'Placeholder for input field to confirm guideline deletion',
          })}
          name="confirmDeleteGuideline"
          onChange={(value) => setDeleteInput(value as string)}
          value={deleteInput}
        />
      </Layout>
    ),
  });

  return (
    <>
      <IconButton
        disabled={disabled}
        icon="trash"
        onClick={() => {
          showConfirmDelete();
        }}
      />
      {confirmDelete}
    </>
  );
};
